import React, { useEffect, useMemo, useState } from "react";
import { VB_TOKENS } from "./tokens";
import { lcg } from "./mathUtils";
import { useReducedMotion } from "./clock";

// =============================================================================
// Atmosphere — production port of Atmosphere.jsx
//
// Composes the cinematic mood layer: void vignette, volumetric god-rays,
// drifting dust, anamorphic lens flare, and a slow camera-breathing wrapper.
// All components are pointer-events:none and intended to overlay the scene.
// =============================================================================

// ----- Shared types ----------------------------------------------------------
type Hue = "warm" | "cool";

interface PassthroughProps {
  style?: React.CSSProperties;
  className?: string;
}

// ----- Mobile breakpoint hook ------------------------------------------------
// Used to scale down particle/ray counts on phones for the <12ms frame budget.
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

// ----- Deterministic per-particle property packs ----------------------------
interface RandSetItem {
  i: number;
  x: number;
  y: number;
  a: number;
  v: number;
  r: number;
  d: number;
}

// Stable RNG bundle — ported from Atmosphere.jsx:9-25 (vbRandSet).
// Produces 6 floats per item (x, y, a, v, r, d) deterministically from seed.
function useRandSet(n: number, seed: number): RandSetItem[] {
  return useMemo(() => {
    const floats = lcg(seed, n * 6);
    const out: RandSetItem[] = [];
    for (let i = 0; i < n; i++) {
      const base = i * 6;
      // `lcg(seed, n*6)` always returns exactly n*6 floats and base+5 < n*6,
      // so these accesses are safe; the `?? 0` satisfies noUncheckedIndexedAccess
      // without changing runtime behavior.
      out.push({
        i,
        x: floats[base] ?? 0,
        y: floats[base + 1] ?? 0,
        a: (floats[base + 2] ?? 0) * Math.PI * 2,
        v: floats[base + 3] ?? 0,
        r: floats[base + 4] ?? 0,
        d: floats[base + 5] ?? 0,
      });
    }
    return out;
  }, [n, seed]);
}

// ----- Vignette + radial fog -------------------------------------------------
// Source ref: Atmosphere.jsx:28-48. Static, no `t` dependency.
export function Vignette({ style, className }: PassthroughProps = {}) {
  const T = VB_TOKENS;
  return (
    <div
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        ...style,
      }}
    >
      {/* base void wash */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 90% 70% at 50% 48%, ${T.void2} 0%, ${T.void1} 35%, ${T.void0} 100%)`,
          pointerEvents: "none",
        }}
      />
      {/* hard vignette around edges */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 80% 60% at 50% 50%, transparent 35%, rgba(0,0,0,0.55) 80%, rgba(0,0,0,0.92) 100%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

// ----- Volumetric god-rays ---------------------------------------------------
// Source ref: Atmosphere.jsx:52-86. Six (or three on mobile) skewed gradient
// stripes blended with `screen` for that dusty light-shaft look.
export interface GodRaysProps extends PassthroughProps {
  t: number;
  intensity?: number;
  /** Optional override; defaults to `useIsMobile()` reducing 6→3 stripes. */
  rayCount?: number;
}

export function GodRays({
  t,
  intensity = 1,
  rayCount,
  style,
  className,
}: GodRaysProps) {
  const reduced = useReducedMotion();
  const isMobile = useIsMobile();
  const count = rayCount ?? (isMobile ? 3 : 6);
  const rays = useRandSet(count, 11);

  // Locked to 1.0 when prefers-reduced-motion is set; otherwise breathes.
  const breathe = reduced ? 1.0 : 0.85 + Math.sin(t * 0.8) * 0.15;

  return (
    <div
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        mixBlendMode: "screen",
        opacity: 0.45 * intensity * breathe,
        ...style,
      }}
    >
      {rays.map((r) => {
        const left = -200 + r.x * 1600;
        const w = 80 + r.r * 220;
        const skew = 18 + r.v * 14;
        const op = 0.10 + r.d * 0.18;
        return (
          <div
            key={r.i}
            style={{
              position: "absolute",
              left,
              top: -200,
              width: w,
              height: "180%",
              transform: `skewX(${skew}deg)`,
              background: `linear-gradient(180deg, transparent 0%, rgba(180,200,235,${op * 0.6}) 30%, rgba(220,235,255,${op}) 60%, transparent 100%)`,
              filter: "blur(8px)",
            }}
          />
        );
      })}
    </div>
  );
}

// ----- Drifting dust particles ----------------------------------------------
// Source ref: Atmosphere.jsx:89-121. Particles drift via a per-frame phase
// term. Per-particle props are computed once via useMemo on stable seeds.
export interface DustParticlesProps extends PassthroughProps {
  t: number;
  count?: number;
  intensity?: number;
}

export function DustParticles({
  t,
  count,
  intensity = 1,
  style,
  className,
}: DustParticlesProps) {
  const reduced = useReducedMotion();
  const isMobile = useIsMobile();
  // Default 60; mobile drops to 20 for the frame-time budget.
  const effectiveCount = count ?? (isMobile ? 20 : 60);
  const dust = useRandSet(effectiveCount, 23);

  return (
    <div
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        mixBlendMode: "screen",
        ...style,
      }}
    >
      {dust.map((d) => {
        // Static positions when reduced motion; otherwise drift.
        let x: number;
        let y: number;
        if (reduced) {
          x = d.x * 100;
          y = d.y * 100;
        } else {
          const period = 8 + d.v * 14;
          const ph = (t / period + d.r) % 1;
          x = (d.x * 100 + ph * 12) % 100;
          y = (d.y * 100 + ph * 25) % 100;
        }
        const size = 0.8 + d.r * 2.2;
        const op = (0.15 + d.d * 0.6) * intensity;
        return (
          <div
            key={d.i}
            style={{
              position: "absolute",
              left: `${x}%`,
              top: `${y}%`,
              width: size,
              height: size,
              borderRadius: "50%",
              background: `rgba(255,240,210,${op})`,
              filter: `blur(${0.4 + d.r}px)`,
              boxShadow: `0 0 ${4 + d.r * 6}px rgba(255,230,180,${op * 0.6})`,
            }}
          />
        );
      })}
    </div>
  );
}

// ----- Lens flare ------------------------------------------------------------
// Source ref: Atmosphere.jsx:124-168. Three overlapping divs:
//   1. 1400×6 horizontal anamorphic streak
//   2. 240×240 radial core glow
//   3. 600×2 sub-streak rotated 35deg
//
// BUG FIX: source had stray `)` in gradient strings at lines 145, 154, 162
// (e.g. `${c}0.7) 50%`). Corrected to proper CSS gradient syntax.
export interface LensFlareProps extends PassthroughProps {
  t: number;
  x: number;
  y: number;
  intensity?: number;
  hue?: Hue;
}

export function LensFlare({
  t,
  x,
  y,
  intensity = 1,
  hue = "warm",
  style,
  className,
}: LensFlareProps) {
  const reduced = useReducedMotion();
  // Locked to 1.0 when prefers-reduced-motion is set; otherwise flickers.
  const flicker = reduced ? 1.0 : 0.92 + Math.sin(t * 9) * 0.08;

  // Hue presets — `cWarm` mimics tungsten/sun, `cCool` mimics neon/cyan rim.
  const cWarm = "rgba(255,220,160,";
  const cCool = "rgba(180,230,255,";
  const c = hue === "cool" ? cCool : cWarm;

  return (
    <div
      className={className}
      style={{
        position: "absolute",
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        width: 0,
        height: 0,
        pointerEvents: "none",
        mixBlendMode: "screen",
        opacity: intensity * flicker,
        ...style,
      }}
    >
      {/* horizontal streak */}
      <div
        style={{
          position: "absolute",
          left: -700,
          top: -3,
          width: 1400,
          height: 6,
          background: `linear-gradient(90deg, transparent, ${c}0.7) 50%, transparent)`,
          filter: "blur(2px)",
        }}
      />
      {/* core glow */}
      <div
        style={{
          position: "absolute",
          left: -120,
          top: -120,
          width: 240,
          height: 240,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${c}0.85) 0%, ${c}0.3) 30%, transparent 60%)`,
          filter: "blur(6px)",
        }}
      />
      {/* diagonal sub-streak */}
      <div
        style={{
          position: "absolute",
          left: -300,
          top: -1,
          width: 600,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${c}0.4) 50%, transparent)`,
          transform: "rotate(35deg)",
          filter: "blur(1px)",
        }}
      />
    </div>
  );
}

// ----- Camera breathing wrapper ----------------------------------------------
// Source ref: Atmosphere.jsx:172-189. Wraps children with a slow translate +
// micro-scale to give the scene a heartbeat.
export interface CameraBreatheProps extends PassthroughProps {
  t: number;
  intensity?: number;
  children?: React.ReactNode;
}

export function CameraBreathe({
  t,
  intensity = 1,
  children,
  style,
  className,
}: CameraBreatheProps) {
  const reduced = useReducedMotion();

  // No transform when reduced motion is preferred.
  const transform = useMemo(() => {
    if (reduced) return "none";
    const breath = Math.sin(t * 0.45);
    const breath2 = Math.sin(t * 0.7 + 1);
    const tx = breath * 4 * intensity;
    const ty = breath2 * 2.5 * intensity;
    const scale = 1 + Math.abs(breath) * 0.008 * intensity;
    return `translate(${tx}px, ${ty}px) scale(${scale})`;
  }, [reduced, t, intensity]);

  return (
    <div
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        transform,
        transformOrigin: "50% 50%",
        transition: "none",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ----- All-in-one atmosphere stack -------------------------------------------
// Source ref: Atmosphere.jsx:192-202. Composes the full mood layer; `tension`
// (0..1) ramps fog/dust intensity for late-round drama. When reduced motion is
// requested, only the static Vignette is rendered.
export interface AtmosphereProps extends PassthroughProps {
  t: number;
  tension?: number;
  lensX?: number;
  lensY?: number;
  hue?: Hue;
}

export function Atmosphere({
  t,
  tension = 0,
  lensX = 0.5,
  lensY = 0.4,
  hue = "warm",
  style,
  className,
}: AtmosphereProps) {
  const reduced = useReducedMotion();

  if (reduced) {
    return <Vignette style={style} className={className} />;
  }

  return (
    <div
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        ...style,
      }}
    >
      <Vignette />
      <GodRays t={t} intensity={0.4 + tension * 0.6} />
      <DustParticles t={t} intensity={0.5 + tension * 0.5} />
      <LensFlare t={t} x={lensX} y={lensY} intensity={tension} hue={hue} />
    </div>
  );
}
